import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { loginUser, registerUser } from "../../redux/slices/authSlice";
import { updateProfile, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../database/firebase";
import { createUserInFirestore } from "../../database/userService";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import authImage from "../../assets/images/login.jpg";
import authImage2 from "../../assets/images/register.jpg";
import logo from "../../assets/whub-logo.png";

function Auth() {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [loading, setLoading] = useState(false);

  // Password visibility toggles
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Form states
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [registerData, setRegisterData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
  });

  const [resetEmail, setResetEmail] = useState("");

  const toggleMode = () => {
    setIsLoginMode((m) => !m);
    setIsForgotPasswordMode(false);
    // Clear form data on toggle
    setLoginData({ email: "", password: "" });
    setRegisterData({
      fullName: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    });
    setResetEmail("");
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!loginData.email || !loginData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      // Use Redux thunk for login
      const result = await dispatch(
        loginUser({
          email: loginData.email,
          password: loginData.password,
        })
      ).unwrap();

      // Clear form
      setLoginData({ email: "", password: "" });

      // Show success message
      toast.success("Login successful!");

      // Navigate to dashboard immediately
      navigate("/", { replace: true });
    } catch (error) {
      let errorMessage = "Failed to login. Please try again.";

      if (error.includes("user-not-found")) {
        errorMessage = "No account found with this email.";
      } else if (error.includes("wrong-password")) {
        errorMessage = "Incorrect password.";
      } else if (error.includes("invalid-email")) {
        errorMessage = "Invalid email address.";
      } else if (error.includes("invalid-credential")) {
        errorMessage = "Invalid email or password.";
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (
      !registerData.fullName ||
      !registerData.email ||
      !registerData.phoneNumber ||
      !registerData.password ||
      !registerData.confirmPassword
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (registerData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      // Use Redux thunk for registration
      const result = await dispatch(
        registerUser({
          email: registerData.email,
          password: registerData.password,
          displayName: registerData.fullName,
          role: "user",
        })
      ).unwrap();

      // Update user profile with display name
      await updateProfile(auth.currentUser, {
        displayName: registerData.fullName,
      });

      // Create user document in Firestore with the comprehensive structure
      await createUserInFirestore(result.uid, {
        name: registerData.fullName,
        email: registerData.email,
        phoneNumber: registerData.phoneNumber,
      });

      // Clear form
      setRegisterData({
        fullName: "",
        email: "",
        phoneNumber: "",
        password: "",
        confirmPassword: "",
      });

      // Show success message
      toast.success("Account created successfully!");

      // Navigate to dashboard immediately
      navigate("/", { replace: true });
    } catch (error) {
      let errorMessage = "Failed to create account. Please try again.";

      if (error.includes("email-already-in-use")) {
        errorMessage = "An account with this email already exists.";
      } else if (error.includes("invalid-email")) {
        errorMessage = "Invalid email address.";
      } else if (error.includes("weak-password")) {
        errorMessage = "Password is too weak. Use at least 6 characters.";
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!resetEmail) {
      toast.error("Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    console.log("Attempting to send password reset email to:", resetEmail);
    
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      console.log("Password reset email sent successfully to:", resetEmail);
      toast.success("Password reset email sent! Check your inbox and spam folder.");
      setResetEmail("");
      setIsForgotPasswordMode(false);
    } catch (error) {
      console.error("Password reset error:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      
      let errorMessage = "Failed to send reset email. Please try again.";

      if (error.code === "auth/user-not-found") {
        errorMessage = "No account found with this email address.";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Invalid email address.";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "Too many requests. Please try again later.";
      } else if (error.code === "auth/missing-email") {
        errorMessage = "Please enter an email address.";
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="relative min-h-screen w-full bg-white overflow-hidden bg-cover bg-center flex items-center justify-center p-4">
        {/* Overlay for better readability */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm hidden md:block"></div>

        <div className="relative z-10 w-full max-w-5xl">
          <div className="bg-white backdrop-blur-md rounded-2xl md:shadow-2xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0 min-h-[600px]">
              {/* Left Column */}
              <div
                className={`relative flex items-center justify-center p-2 transition-all duration-1000 ease-in-out ${
                  isLoginMode ? "bg-white" : "bg-white"
                }`}
              >
                {/* Login Form */}
                <div
                  className={`w-full max-w-md transition-all duration-700 ease-in-out ${
                    isLoginMode && !isForgotPasswordMode
                      ? "opacity-100 translate-x-0 scale-100 pointer-events-auto visible relative"
                      : "opacity-0 -translate-x-16 scale-95 pointer-events-none invisible absolute"
                  }`}
                >
                  <div className="flex items-center justify-center pb-5">
                    <img src={logo} alt="" className="h-28 w-28 " />
                  </div>
                  <div className="pb-5">
                    <h2 className="text-lg md:text-4xl font-bold mb-2 text-black text-center md:text-left">
                      Login Now To Your Account
                    </h2>
                    <p className="text-sm md:text-md text-neutral-700 mb-4 text-center md:text-left">
                      Access your account to manage a settings, explore
                      features.
                    </p>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={loginData.email}
                        onChange={(e) =>
                          setLoginData({
                            ...loginData,
                            email: e.target.value,
                          })
                        }
                        placeholder="Enter your email"
                        className="w-full py-3 px-4 rounded-lg border-2 border-gray-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Password
                        </label>
                        <button
                          type="button"
                          onClick={() => setIsForgotPasswordMode(true)}
                          className="text-sm text-primary hover:underline font-medium"
                          disabled={loading}
                        >
                          Forgot Password?
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          type={showLoginPassword ? "text" : "password"}
                          value={loginData.password}
                          onChange={(e) =>
                            setLoginData({
                              ...loginData,
                              password: e.target.value,
                            })
                          }
                          placeholder="Enter your password"
                          className="w-full py-3 px-4 pr-12 rounded-lg border-2 border-gray-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                          disabled={loading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword((v) => !v)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                          tabIndex={-1}
                        >
                          {showLoginPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-primary text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {loading ? "Signing In..." : "Sign In"}
                    </button>
                    <div class="my-4 flex items-center gap-4">
                      <hr class="w-full border-slate-300" />
                      <p class="text-sm text-slate-900 text-center">or</p>
                      <hr class="w-full border-slate-300" />
                    </div>
                    <div>
                      <div class="space-x-8 flex justify-center">
                        <button
                          type="button"
                          class="border-0 outline-0 cursor-pointer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-6 h-6 inline"
                            viewBox="0 0 512 512"
                          >
                            <path
                              fill="#fbbd00"
                              d="M120 256c0-25.367 6.989-49.13 19.131-69.477v-86.308H52.823C18.568 144.703 0 198.922 0 256s18.568 111.297 52.823 155.785h86.308v-86.308C126.989 305.13 120 281.367 120 256z"
                              data-original="#fbbd00"
                            />
                            <path
                              fill="#0f9d58"
                              d="m256 392-60 60 60 60c57.079 0 111.297-18.568 155.785-52.823v-86.216h-86.216C305.044 385.147 281.181 392 256 392z"
                              data-original="#0f9d58"
                            />
                            <path
                              fill="#31aa52"
                              d="m139.131 325.477-86.308 86.308a260.085 260.085 0 0 0 22.158 25.235C123.333 485.371 187.62 512 256 512V392c-49.624 0-93.117-26.72-116.869-66.523z"
                              data-original="#31aa52"
                            />
                            <path
                              fill="#3c79e6"
                              d="M512 256a258.24 258.24 0 0 0-4.192-46.377l-2.251-12.299H256v120h121.452a135.385 135.385 0 0 1-51.884 55.638l86.216 86.216a260.085 260.085 0 0 0 25.235-22.158C485.371 388.667 512 324.38 512 256z"
                              data-original="#3c79e6"
                            />
                            <path
                              fill="#cf2d48"
                              d="m352.167 159.833 10.606 10.606 84.853-84.852-10.606-10.606C388.668 26.629 324.381 0 256 0l-60 60 60 60c36.326 0 70.479 14.146 96.167 39.833z"
                              data-original="#cf2d48"
                            />
                            <path
                              fill="#eb4132"
                              d="M256 120V0C187.62 0 123.333 26.629 74.98 74.98a259.849 259.849 0 0 0-22.158 25.235l86.308 86.308C162.883 146.72 206.376 120 256 120z"
                              data-original="#eb4132"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          class="border-0 outline-0 cursor-pointer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-6 h-6"
                            fill="#000"
                            viewBox="0 0 22.773 22.773"
                          >
                            <path
                              d="M15.769 0h.162c.13 1.606-.483 2.806-1.228 3.675-.731.863-1.732 1.7-3.351 1.573-.108-1.583.506-2.694 1.25-3.561C13.292.879 14.557.16 15.769 0zm4.901 16.716v.045c-.455 1.378-1.104 2.559-1.896 3.655-.723.995-1.609 2.334-3.191 2.334-1.367 0-2.275-.879-3.676-.903-1.482-.024-2.297.735-3.652.926h-.462c-.995-.144-1.798-.932-2.383-1.642-1.725-2.098-3.058-4.808-3.306-8.276v-1.019c.105-2.482 1.311-4.5 2.914-5.478.846-.52 2.009-.963 3.304-.765.555.086 1.122.276 1.619.464.471.181 1.06.502 1.618.485.378-.011.754-.208 1.135-.347 1.116-.403 2.21-.865 3.652-.648 1.733.262 2.963 1.032 3.723 2.22-1.466.933-2.625 2.339-2.427 4.74.176 2.181 1.444 3.457 3.028 4.209z"
                              data-original="#000000"
                            ></path>
                          </svg>
                        </button>
                        <button
                          type="button"
                          class="border-0 outline-0 cursor-pointer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-6 h-6"
                            fill="#007bff"
                            viewBox="0 0 167.657 167.657"
                          >
                            <path
                              d="M83.829.349C37.532.349 0 37.881 0 84.178c0 41.523 30.222 75.911 69.848 82.57v-65.081H49.626v-23.42h20.222V60.978c0-20.037 12.238-30.956 30.115-30.956 8.562 0 15.92.638 18.056.919v20.944l-12.399.006c-9.72 0-11.594 4.618-11.594 11.397v14.947h23.193l-3.025 23.42H94.026v65.653c41.476-5.048 73.631-40.312 73.631-83.154 0-46.273-37.532-83.805-83.828-83.805z"
                              data-original="#010002"
                            ></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="text-center pt-4">
                      <button
                        onClick={toggleMode}
                        type="button"
                        disabled={loading}
                        className="text-gray-500 font-medium hover:underline transition-all disabled:opacity-50"
                      >
                        Don't have an account?{" "}
                        <span className="font-bold text-primary">Register</span>
                      </button>
                    </div>
                  </form>
                </div>

                {/* Forgot Password Form */}
                <div
                  className={`w-full max-w-md transition-all duration-700 ease-in-out ${
                    isForgotPasswordMode
                      ? "opacity-100 translate-x-0 scale-100 pointer-events-auto visible relative"
                      : "opacity-0 -translate-x-16 scale-95 pointer-events-none invisible absolute"
                  }`}
                >
                  <div className="flex items-center justify-center pb-5">
                    <img src={logo} alt="" className="h-28 w-28 " />
                  </div>
                  <div className="pb-5">
                    <h2 className="text-lg md:text-4xl font-bold mb-2 text-black text-center md:text-left">
                      Reset Your Password
                    </h2>
                    <p className="text-sm md:text-md text-neutral-700 mb-4 text-center md:text-left">
                      Enter your email address and we'll send you a link to reset your password.
                    </p>
                  </div>

                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full py-3 px-4 rounded-lg border-2 border-gray-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                        disabled={loading}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-primary text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {loading ? "Sending..." : "Send Reset Link"}
                    </button>

                    <div className="text-center pt-4">
                      <button
                        onClick={() => {
                          setIsForgotPasswordMode(false);
                          setResetEmail("");
                        }}
                        type="button"
                        disabled={loading}
                        className="text-gray-500 font-medium hover:underline transition-all disabled:opacity-50"
                      >
                        Back to{" "}
                        <span className="font-bold text-primary">Login</span>
                      </button>
                    </div>
                  </form>
                </div>

                {/* Image for Register mode (left side) */}
                <div className="hidden md:block">
                  <div
                    className={`flex items-center justify-center transition-all duration-1000 ease-in-out ${
                      !isLoginMode
                        ? "opacity-100 translate-x-0 scale-100 visible relative"
                        : "opacity-0 translate-x-16 scale-95 invisible absolute"
                    }`}
                  >
                    <img
                      src={authImage}
                      alt="Register Illustration"
                      className="w-full md:w-[500px] max-w-md rounded-2xl h-full object-cover drop-shadow-2xl"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div
                className={`relative flex items-center justify-center p-2 transition-all duration-1000 ease-in-out ${
                  isLoginMode ? "bg-white" : "bg-white"
                }`}
              >
                {/* Register Form */}
                <div
                  className={`w-full max-w-md transition-all duration-700 ease-in-out ${
                    !isLoginMode
                      ? "opacity-100 translate-x-0 scale-100 pointer-events-auto visible relative"
                      : "opacity-0 translate-x-16 scale-95 pointer-events-none invisible absolute"
                  }`}
                >
                  <div className="flex items-center justify-center pb-5">
                    <img src={logo} alt="" className="text-center h-28 w-28 " />
                  </div>
                  <div className="pb-5">
                    <h2 className="block text-lg md:text-4xl font-bold mb-2 text-black text-center md:text-left">
                      Create Account
                    </h2>
                    <p className="text-sm md:text-md text-gray-600 text-center md:text-left mb-4">
                      Create an account to unlock a feature and get most out of
                      a knowledge
                    </p>
                  </div>

                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={registerData.fullName}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            fullName: e.target.value,
                          })
                        }
                        placeholder="Enter your full name"
                        className="w-full py-3 px-4 rounded-lg border-2 border-gray-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block  text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={registerData.email}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            email: e.target.value,
                          })
                        }
                        placeholder="Enter your email"
                        className="w-full py-3 px-4 rounded-lg border-2 border-gray-200 bg-white transition-all outline-none"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block  text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={registerData.phoneNumber}
                        onChange={(e) =>
                          setRegisterData({
                            ...registerData,
                            phoneNumber: e.target.value,
                          })
                        }
                        placeholder="Enter your phone number"
                        className="w-full py-3 px-4 rounded-lg border-2 border-gray-200 bg-white transition-all outline-none"
                        disabled={loading}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-x-2">
                      <div>
                        <label className="block  text-sm font-medium text-gray-700 mb-2">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type={showRegisterPassword ? "text" : "password"}
                            value={registerData.password}
                            onChange={(e) =>
                              setRegisterData({
                                ...registerData,
                                password: e.target.value,
                              })
                            }
                            placeholder="Create a password"
                            className="w-full py-3 px-4 pr-10 rounded-lg border-2 border-gray-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegisterPassword((v) => !v)}
                            className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400 hover:text-gray-600 transition-colors"
                            tabIndex={-1}
                          >
                            {showRegisterPassword ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block  text-sm font-medium text-gray-700 mb-2">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <input
                            type={showRegisterConfirmPassword ? "text" : "password"}
                            value={registerData.confirmPassword}
                            onChange={(e) =>
                              setRegisterData({
                                ...registerData,
                                confirmPassword: e.target.value,
                              })
                            }
                            placeholder="Confirm your password"
                            className="w-full py-3 px-4 pr-10 rounded-lg border-2 border-gray-200 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                            disabled={loading}
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegisterConfirmPassword((v) => !v)}
                            className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400 hover:text-gray-600 transition-colors"
                            tabIndex={-1}
                          >
                            {showRegisterConfirmPassword ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 bg-primary text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {loading ? "Creating Account..." : "Create Account"}
                    </button>
                    <div class="my-4 flex items-center gap-4">
                      <hr class="w-full border-slate-300" />
                      <p class="text-sm text-slate-900 text-center">or</p>
                      <hr class="w-full border-slate-300" />
                    </div>
                    <div>
                      <div class="space-x-8 flex justify-center">
                        <button
                          type="button"
                          class="border-0 outline-0 cursor-pointer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-6 h-6 inline"
                            viewBox="0 0 512 512"
                          >
                            <path
                              fill="#fbbd00"
                              d="M120 256c0-25.367 6.989-49.13 19.131-69.477v-86.308H52.823C18.568 144.703 0 198.922 0 256s18.568 111.297 52.823 155.785h86.308v-86.308C126.989 305.13 120 281.367 120 256z"
                              data-original="#fbbd00"
                            />
                            <path
                              fill="#0f9d58"
                              d="m256 392-60 60 60 60c57.079 0 111.297-18.568 155.785-52.823v-86.216h-86.216C305.044 385.147 281.181 392 256 392z"
                              data-original="#0f9d58"
                            />
                            <path
                              fill="#31aa52"
                              d="m139.131 325.477-86.308 86.308a260.085 260.085 0 0 0 22.158 25.235C123.333 485.371 187.62 512 256 512V392c-49.624 0-93.117-26.72-116.869-66.523z"
                              data-original="#31aa52"
                            />
                            <path
                              fill="#3c79e6"
                              d="M512 256a258.24 258.24 0 0 0-4.192-46.377l-2.251-12.299H256v120h121.452a135.385 135.385 0 0 1-51.884 55.638l86.216 86.216a260.085 260.085 0 0 0 25.235-22.158C485.371 388.667 512 324.38 512 256z"
                              data-original="#3c79e6"
                            />
                            <path
                              fill="#cf2d48"
                              d="m352.167 159.833 10.606 10.606 84.853-84.852-10.606-10.606C388.668 26.629 324.381 0 256 0l-60 60 60 60c36.326 0 70.479 14.146 96.167 39.833z"
                              data-original="#cf2d48"
                            />
                            <path
                              fill="#eb4132"
                              d="M256 120V0C187.62 0 123.333 26.629 74.98 74.98a259.849 259.849 0 0 0-22.158 25.235l86.308 86.308C162.883 146.72 206.376 120 256 120z"
                              data-original="#eb4132"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          class="border-0 outline-0 cursor-pointer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-6 h-6"
                            fill="#000"
                            viewBox="0 0 22.773 22.773"
                          >
                            <path
                              d="M15.769 0h.162c.13 1.606-.483 2.806-1.228 3.675-.731.863-1.732 1.7-3.351 1.573-.108-1.583.506-2.694 1.25-3.561C13.292.879 14.557.16 15.769 0zm4.901 16.716v.045c-.455 1.378-1.104 2.559-1.896 3.655-.723.995-1.609 2.334-3.191 2.334-1.367 0-2.275-.879-3.676-.903-1.482-.024-2.297.735-3.652.926h-.462c-.995-.144-1.798-.932-2.383-1.642-1.725-2.098-3.058-4.808-3.306-8.276v-1.019c.105-2.482 1.311-4.5 2.914-5.478.846-.52 2.009-.963 3.304-.765.555.086 1.122.276 1.619.464.471.181 1.06.502 1.618.485.378-.011.754-.208 1.135-.347 1.116-.403 2.21-.865 3.652-.648 1.733.262 2.963 1.032 3.723 2.22-1.466.933-2.625 2.339-2.427 4.74.176 2.181 1.444 3.457 3.028 4.209z"
                              data-original="#000000"
                            ></path>
                          </svg>
                        </button>
                        <button
                          type="button"
                          class="border-0 outline-0 cursor-pointer"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="w-6 h-6"
                            fill="#007bff"
                            viewBox="0 0 167.657 167.657"
                          >
                            <path
                              d="M83.829.349C37.532.349 0 37.881 0 84.178c0 41.523 30.222 75.911 69.848 82.57v-65.081H49.626v-23.42h20.222V60.978c0-20.037 12.238-30.956 30.115-30.956 8.562 0 15.92.638 18.056.919v20.944l-12.399.006c-9.72 0-11.594 4.618-11.594 11.397v14.947h23.193l-3.025 23.42H94.026v65.653c41.476-5.048 73.631-40.312 73.631-83.154 0-46.273-37.532-83.805-83.828-83.805z"
                              data-original="#010002"
                            ></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="text-center pt-4">
                      <button
                        onClick={toggleMode}
                        type="button"
                        disabled={loading}
                        className="text-gray-600 font-medium hover:underline transition-all disabled:opacity-50"
                      >
                        Already have an account?{" "}
                        <span className="font-bold text-primary">Sign In</span>
                      </button>
                    </div>
                  </form>
                </div>

                {/* Image for Login mode (right side) */}
                <div className="hidden md:block">
                  <div
                    className={`flex items-center justify-center transition-all duration-1000 ease-in-out ${
                      isLoginMode
                        ? "opacity-100 translate-x-0 scale-100 visible relative"
                        : "opacity-0 -translate-x-16 scale-95 invisible absolute"
                    }`}
                  >
                    <img
                      src={authImage2}
                      alt="Login Illustration"
                      className="w-full md:w-full max-w-md h-full rounded-2xl object-fill drop-shadow-2xl"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Auth;
